<script>
import { Checks } from '@/utils/Checks'
import { getErrorMessage, JYPromiseAllSettledPolyfill } from '@/utils/utils'

/**
 * 上传文件组件
 */

export default {
  props: {
    multiple: Boolean, // 是否允许一次选择多个文件, true为允许, 反之不允许
    accept: String, // 允许的文件类型, 例如 'image/*' 等
    onStart: Function, // 进行源文件预处理
    fileList: Array, // 预处理完成后的文件列表
    autoUpload: Boolean, // 是否选择完图片后立即自动上传, true为自动上传, 反之不会自动上传
    isLoading: Boolean, // 是否有图片正在上传
    limit: Number, // 限制一次选择文件的数量
    onExceed: Function, // 文件数量超出限制的钩子
    uploadToFileServerFirst: Boolean, // 是否先将原文件上传至文件服务器, true为上传至文件服务器, 反之不上传
    uploadSingleFileOneTime: Boolean, // 是否一次请求只上传单个文件, true为是，反之每个请求会上传多张照片
    uploadToFileServerMethod: Function, // 上传至文件服务器的方法, 返回值必须为promise实例
    httpRequest: Function, // 上传至接口的方法, 返回值必须为promise实例
    acceptTypes: Array, // 允许上传的具体文件类型
    limitSize: Number, // 允许上传的文件字节数最大值
    onUnexpectedFileType: Function, // 文件类型超出限制钩子
    onUnexpectedFileSize: Function // 文件大小超出限制钩子
  },

  methods: {
    validFileType (file) {
      return Checks.isNotEmptyArray(this.acceptTypes) ? this.acceptTypes.includes(file.type) : true
    },

    validFileSize (file) {
      return (Checks.isInteger(this.limitSize) && this.limitSize > 0) ? Number(file.size) <= this.limitSize : true
    },

    setLoading (isLoading) {
      this.$emit('switch-loading-status', isLoading)
    },

    /**
     * 处理单个文件的函数流
     * @param {Array} functions 按照一定顺序排列的函数序列
     * @returns {Function} 可以按照特定函数顺序依次处理单个文件的函数
     */
    pipe (functions) {
      return file => functions.reduce((prev, next) => {
        if (prev && prev.then) {
          return prev.then(next, (err) => Promise.reject(err))
        } else {
          return Promise.resolve(prev).then(next, (err) => Promise.reject(err))
        }
      }, Promise.resolve(file))
    },

    // 更新文件上传之后的状态
    updateFileInfo (params) {
      let { file, status, tipText } = params

      if (!(file && file.uid)) {
        return
      }

      let index = this.fileList.findIndex(it => it.uid === file.uid)

      if (index === -1) {
        return
      }

      this.fileList[index].status = status
      this.fileList[index].tipText = tipText
      this.$emit('update-file-status', {index, file: this.fileList[index]})
    },

    handleFileClick () {
      this.$refs['file-input'].value = null
      this.$refs['file-input'].click()
    },

    handleFolderClick () {
      this.$refs['folder-input'].value = null
      this.$refs['folder-input'].click()
    },

    handleFileChange (ev) {
      const files = ev.target.files
      console.log('handleFileChange files: ', files)

      if (!files) {
        return
      }
      this.uploadFiles(files)
    },

    handleFolderChange (ev) {
      const files = ev.target.files
      console.log('handleFolderChange files: ', files)

      if (!files) {
        return
      }
      this.uploadFiles(files)
    },

    // 处理选中的所有文件
    uploadFiles (files) {
      if (this.limit && this.fileList.length + files.length > this.limit) {
        this.onExceed && this.onExceed(files, this.fileList)
        return
      }

      if (this.isLoading) {
        return
      }

      this.setLoading(true)

      try {
        let postFiles = Array.prototype.slice.call(files)
        if (!this.multiple) {
          postFiles = postFiles.slice(0, 1)
        }

        if (postFiles.length === 0) {
          return
        }

        postFiles.forEach(rawFile => {
          if (!this.validFileType(rawFile)) { // 文件类型未通过验证
            this.onUnexpectedFileType && this.onUnexpectedFileType(rawFile)
            return
          }

          if (!this.validFileSize(rawFile)) { // 文件大小未通过验证
            this.onUnexpectedFileSize && this.onUnexpectedFileSize(rawFile)
            return
          }

          this.onStart(rawFile)
        })

        this.$nextTick(() => {
          if (this.fileList.length === 0) {
            this.setLoading(false)
            return
          }

          if (this.autoUpload) {
            this.beforeUpload(this.fileList)
          } else {
            this.setLoading(false)
          }
        })
      } catch (error) {
        console.error('jy-multiple-upload inner-upload-vue uploadFiles err: ', error)
        this.setLoading(false)
        this.onError && this.onError(error, files, this.fileList)
      }
    },

    beforeUpload (fileList) {
      this.setLoading(true)

      fileList = fileList.filter(f => f.status !== 'success')
      if (fileList.length === 0) {
        this.setLoading(false)
        return
      }

      if (this.uploadSingleFileOneTime) {
        this.uploadSingleFile(fileList)
      } else {
        this.uploadFileList(fileList)
      }
    },

    uploadSingleFile (fileList) {
      let functionQueue = [this.httpRequest]

      if (this.uploadToFileServerFirst) {
        functionQueue.unshift(this.uploadToFileServerMethod)
      }

      JYPromiseAllSettledPolyfill(
        fileList.map((file) => this.pipe(functionQueue)(file))
      ).then(res => {
        console.log(res)
        res.forEach((it, index) => {
          if (it.status === 'fulfilled') {
            this.updateFileInfo({
              file: fileList[index],
              status: 'success',
              tipText: '已上传'
            })
          } else if (it.status === 'rejected') {
            this.updateFileInfo({
              file: fileList[index],
              status: 'fail',
              tipText: '上传失败！' + getErrorMessage(it.reason)
            })
          }
        })

        this.setLoading(false)
      }).catch(err => {
        this.setLoading(false)
        this.onError && this.onError(err, this.fileList)
      })
    },

    uploadFileList (fileList) {} // TODO: implement me later
  },

  render (h) {
    let {
      handleFileClick,
      handleFolderClick,
      handleFileChange,
      handleFolderChange,
      multiple,
      accept,
      isLoading
    } = this

    const fileInputData = {
      class: {
        'multiple-upload': true
      },
      on: {
        click: handleFileClick
      }
    }

    const folderInputData = {
      class: {
        'multiple-upload': true
      },
      on: {
        click: handleFolderClick
      }
    }

    return (
      <div tabindex="0">
        <div {...fileInputData}>
          {this.$slots.trigger}
          <input disabled={isLoading} class="multiple-upload__input" type="file" ref="file-input" on-change={handleFileChange} multiple={multiple} accept={accept}></input>
        </div>
        <div {...folderInputData}>
          {this.$slots.folderTrigger}
          <input disabled={isLoading} class="multiple-upload__input" type="file" ref="folder-input" on-change={handleFolderChange} webkitdirectory directory mozdirectory odirectory msdirectory></input>
        </div>
      </div>
    )
  }
}
</script>

<style lang="less" scoped>
.multiple-upload {
  display: inline-block;
  text-align: center;
  cursor: pointer;
  outline: none;
}
.multiple-upload__input {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/components/jy-multiple-upload/src/upload.vue